import {defineStore} from 'pinia'
import {useRouter} from 'vue-router'

const LOCK_TIMEOUT = 10 * 60 * 1000 // 10 mins
const STORAGE_KEYS = {
    PWA_PIN_HASH: 'pwa_pin_hash',
    LAST_ACTIVITY: 'last_activity',
    REFRESH_TOKEN: 'refreshToken',
    PIN_SALT: 'pin_salt'
}

const generateSalt = async () => {
    const array = new Uint8Array(16)
    window.crypto.getRandomValues(array)
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('')
}

const hashPin = async (pin, salt) => {
    const encoder = new TextEncoder()
    const data = encoder.encode(pin + salt)
    const hashBuffer = await crypto.subtle.digest('SHA-256', data)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    return hashArray.map(byte => byte.toString(16).padStart(2, '0')).join('')
}

export const useAuthStore = defineStore('auth', {
    state: () => ({
        isLoggedIn: false,
        isPwaUnlocked: false,
        lastActivityTimestamp: Date.now(),
        lockoutUntil: null,
        attemptCount: 0
    }),

    getters: {
        isLocked: (state) => {
            return state.lockoutUntil && Date.now() < state.lockoutUntil
        },

        shouldShowUnlockScreen: (state) => {
            return !state.isPwaUnlocked && !!localStorage.getItem(STORAGE_KEYS.PWA_PIN_HASH)
        }
    },

    actions: {
        setLoggedIn(status) {
            this.isLoggedIn = status
        },

        resetActivityTimer() {
            const now = Date.now()
            this.lastActivityTimestamp = now
            if (this.isPwaUnlocked) {
                localStorage.setItem(STORAGE_KEYS.LAST_ACTIVITY, now.toString())
            }
        },

        setPwaUnlocked(status) {
            this.isPwaUnlocked = status
            if (!status) {
                this.attemptCount = 0
                this.lockoutUntil = null
            }
        },

        clearSensitiveData() {
            localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN)
            this.isLoggedIn = false
            this.isPwaUnlocked = false
            this.attemptCount = 0
            this.lockoutUntil = null
        },
        logOut() {
            this.clearSensitiveData()
            // QR signing keys are bearer secrets; they leave with the session.
            // Imported lazily: qrKeyCache -> api -> authStore would be a cycle.
            import('@/services/qrKeyCache.js')
                .then(m => m.clearQrKeyCache())
                .catch(() => {})
            // Logouts that skip api.logout (lockout, loading failure, wrong brand):
            // the browser still unsubscribes this push device; the API drops the
            // row on the next push's 410. A no-op after api.logout already removed it.
            import('@/services/pushDevice.js')
                .then(m => m.removePushDevice({server: false}))
                .catch(() => {})
            // The next staff member to sign in gets their own reachability.
            import('@/stores/pushReachability.js')
                .then(m => m.usePushReachabilityStore().reset())
                .catch(() => {})
            // Their inbox and the app badge leave with them (#141).
            import('@/stores/inbox.js')
                .then(m => m.useInboxStore().reset())
                .catch(() => {})
            localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN)
            localStorage.removeItem(STORAGE_KEYS.PIN_SALT)
            localStorage.removeItem(STORAGE_KEYS.PWA_PIN_HASH)
            localStorage.removeItem('last_activity')
            localStorage.removeItem('pwaInstallDeclined')
            localStorage.removeItem('weekId')
        },

        async checkLoginStatus() {
            if (this.isLoggedIn) return true

            const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN)
            if (refreshToken) {
                this.setLoggedIn(true)
                return true
            }

            return false
        },

        async setPwaPin(pin) {
            if (!pin || pin.length !== 4 || !/^\d{4}$/.test(pin)) {
                throw new Error('Invalid PIN format')
            }

            try {
                const salt = await generateSalt()
                const hashedPin = await hashPin(pin, salt)

                localStorage.setItem(STORAGE_KEYS.PIN_SALT, salt)
                localStorage.setItem(STORAGE_KEYS.PWA_PIN_HASH, hashedPin)
                this.setPwaUnlocked(true)
                return true
            } catch (error) {
                console.error('Error setting PIN:', error)
                throw new Error('Failed to set PIN')
            }
        },

        async verifyPwaPin(pin) {
            if (this.isLocked) {
                return false
            }

            try {
                const salt = localStorage.getItem(STORAGE_KEYS.PIN_SALT)
                const storedHash = localStorage.getItem(STORAGE_KEYS.PWA_PIN_HASH)

                if (!salt || !storedHash) {
                    return false
                }

                const hashedInputPin = await hashPin(pin, salt)
                const isValid = storedHash === hashedInputPin

                if (!isValid) {
                    this.attemptCount++
                    if (this.attemptCount >= 5) {
                        const lockoutDuration = Math.min(Math.pow(2, this.attemptCount - 5) * 1000, 30 * 60 * 1000)
                        this.lockoutUntil = Date.now() + lockoutDuration
                    }
                } else {
                    this.attemptCount = 0
                    this.lockoutUntil = null
                    this.setPwaUnlocked(true)
                }

                return isValid
            } catch (error) {
                console.error('Error verifying PIN:', error)
                return false
            }
        },

        async isPinSet() {
            return !!localStorage.getItem(STORAGE_KEYS.PWA_PIN_HASH)
        },

        checkPwaLock() {
            if (this.isPwaUnlocked) {
                return true
            }
            const now = Date.now()
            const lastActivity = parseInt(localStorage.getItem(STORAGE_KEYS.LAST_ACTIVITY), 10)
            if (lastActivity && (now - lastActivity <= LOCK_TIMEOUT)) {
                return true
            }

            this.setPwaUnlocked(false)
            return false
        }
    }
})